import type { FieldErrors, FieldPath } from 'react-hook-form'
import { formFieldOf, itemFieldOf } from '@/features/consignment-form/api-payload'
import { createIssueSchema } from '@/features/consignment-form/contract'
import type { BackendError, CreateIssue } from '@/features/consignment-form/contract'
import type { Consignment, Item } from '@/features/consignment-form/schema'
import { t } from '@/lang'

/**
 * Every label the form puts on a field, keyed by the path that field validates under, in the
 * order the form shows them. Single source: the sections render these and the submit error
 * summary quotes them, so a renamed label cannot leave the summary saying the old word.
 */
export const fieldLabels = {
  'shipper.name': t('field.shipper.name'),
  'shipper.surname': t('field.shipper.surname'),
  'shipper.contactNumber': t('field.shipper.contact-number'),
  'shipper.email': t('field.shipper.email'),
  'shipper.legalEntity': t('field.shipper.legal-entity'),
  'shipper.companyName': t('field.company-name'),
  'shipper.street': t('field.street'),
  'shipper.postcode': t('field.postcode'),
  'shipper.locality': t('field.locality'),
  departurePort: t('field.departure-port'),
  readyFrom: t('field.ready-from'),
  paidBy: t('field.paid-by'),
  paymentMethod: t('field.payment-method'),
  'consignee.name': t('field.consignee.name'),
  'consignee.surname': t('field.consignee.surname'),
  'consignee.contactNumber': t('field.consignee.contact-number'),
  'consignee.email': t('field.consignee.email'),
  'consignee.legalEntity': t('field.consignee.legal-entity'),
  'consignee.companyName': t('field.company-name'),
  'consignee.street': t('field.street'),
  'consignee.postcode': t('field.postcode'),
  'consignee.locality': t('field.locality'),
  items: t('field.items'),
} as const satisfies Partial<Record<FieldPath<Consignment>, string>>

/** Labels of the item editor's fields; the editor renders these and a rejected item quotes them. */
export const itemLabels = {
  qty: t('item.qty'),
  category: t('item.category'),
  description: t('item.description'),
  weightKg: t('item.weight'),
  lengthCm: t('item.length'),
  widthCm: t('item.width'),
  heightCm: t('item.height'),
} as const satisfies Record<keyof Item, string>

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const at = (node: unknown, key: string): unknown => (isRecord(node) ? node[key] : undefined)

const messageAt = (errors: FieldErrors<Consignment>, path: string) => {
  const message = at(path.split('.').reduce<unknown>(at, errors), 'message')
  return typeof message === 'string' ? message : undefined
}

const sharedLabels = new Set<string>(
  Object.values(fieldLabels).filter((label, index, all) => all.indexOf(label) !== index)
)

const lineLabel = (path: string, label: string) => {
  const [owner] = path.split('.')
  return (owner === 'shipper' || owner === 'consignee') && sharedLabels.has(label)
    ? `${t(`party.${owner}`)} — ${label}`
    : label
}

export function errorSummary(errors: FieldErrors<Consignment>) {
  return Object.entries(fieldLabels).flatMap(([path, label]) => {
    const message = messageAt(errors, path)
    return message === undefined ? [] : [{ path, label: lineLabel(path, label), message }]
  })
}

/**
 * One line of a rejected create for the message banner: the form's label for the field f-edge named, then
 * f-edge's own words. Backend text never goes under a field, where its unknown length could break the layout.
 */
export function rejectionLine({ field, message }: CreateIssue) {
  const place = formFieldOf(field)
  if (!place) return `${field}: ${message}`
  return 'path' in place
    ? `${lineLabel(place.path, fieldLabels[place.path])}: ${message}`
    : `${t('item.number', { number: place.item + 1 })} — ${itemLabels[place.field]}: ${message}`
}

/**
 * A backend error as the message banner shows it: "400 CODE" (the title where there is no code) as the
 * headline, and what is wrong listed under it. A detail shaped "path: message" gets the field's label.
 */
export function formatBackendError(
  { status, code, title, detail, fieldErrors = [] }: BackendError,
  lineOf: (issue: CreateIssue) => string = rejectionLine
) {
  const headline = [status, code ?? title].filter(Boolean).join(' ')
  if (fieldErrors.length > 0) return { headline, lines: fieldErrors.map(lineOf) }
  if (!detail) return { headline, lines: [] }
  const [, field, message] = /^([^:]+): (.+)$/s.exec(detail) ?? []
  const issue = createIssueSchema.safeParse({ field, message })
  return { headline, lines: [issue.success ? lineOf(issue.data) : detail] }
}

/** A field f-edge refused an estimate for, named by the item editor's label. */
export function itemIssueLine({ field, message }: CreateIssue) {
  const item = itemFieldOf(field)
  return `${item ? itemLabels[item] : field}: ${message}`
}
